/**
 * InboundBadgeList — renders §10 badge descriptors as coloured pills. The text
 * comes exclusively from the `posteingang` i18n namespace (whitelist-approved
 * wording); this component only maps tone → colour classes. WL-A6.
 *
 * The `success` tone is exclusively the `eInvoiceValid` badge (the ONE "EN
 * 16931 Basisprüfung bestanden" classification, see `deriveInboundBadges`) —
 * it is styled as a Mint-tinted "seal" with a checkmark icon (polish pass
 * posteingang-polish) instead of a flat fill, to read as a trust signal.
 * Badge TEXT is untouched — only the tone → colour/icon mapping changed.
 *
 * @module InboundBadgeList
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconSuccess } from '../../icons';
import type {
  InboundBadgeDescriptor,
  InboundBadgeTone,
} from '../../utils/inboundAdapters';

interface InboundBadgeListProps {
  badges: InboundBadgeDescriptor[];
}

/**
 * Tailwind classes per tone (colour only — no text). v5
 * (architecture-ux-design-v5.md §5/§5.1): this was a FORK of
 * `common/Badge`'s `TONE_CLASSES` — kept in sync here so the app has one
 * status colour language, not two. Values now reference the same
 * `--tone-{tone}-{bg,fg,border}` CSS vars as `Badge`/`StatusChip` (defined in
 * `styles/tokens.css`). `success` is Laub (green ramp), NOT Mint — Mint is
 * marque, not status (v5 §1.3).
 */
const TONE_CLASSES: Record<InboundBadgeTone, string> = {
  success: 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border-[var(--tone-success-border)]',
  info: 'bg-[var(--tone-info-bg)] text-[var(--tone-info-fg)] border-[var(--tone-info-border)]',
  warning: 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)] border-[var(--tone-warning-border)]',
  neutral: 'bg-[var(--tone-neutral-bg)] text-[var(--tone-neutral-fg)] border-[var(--tone-neutral-border)]',
  // IDV-4: distinct from `warning` — an ambiguous/mixed tax-category "bitte
  // prüfen" signal, not a confirmed §13b/§1a self-assessment obligation.
  caution: 'bg-[var(--tone-caution-bg)] text-[var(--tone-caution-fg)] border-[var(--tone-caution-border)]',
};

const InboundBadgeList: React.FC<InboundBadgeListProps> = ({ badges }) => {
  const { t } = useTranslation('posteingang');
  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((b) => (
        <span
          key={b.id}
          data-badge-id={b.id}
          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium tracking-[0.01em] ${TONE_CLASSES[b.tone]}`}
        >
          {b.tone === 'success' && <IconSuccess size={10} />}
          {t(b.i18nKey, b.params)}
        </span>
      ))}
    </div>
  );
};

export default InboundBadgeList;
